/**
 * DriverMissionDetail - Mission detail with trajet timeline and action buttons
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, CheckCircle, Play, MapPin,
  AlertCircle, Download
} from 'lucide-react';
import { driverMonoAPI } from '../../../api/missions.js';
import { getAuthHeaders, API_BASE_URL } from '../../../api/core.js';
import MissionStatusBadge from '../missions/MissionStatusBadge.jsx';
import { useFormat } from '../../../i18n/format.js';

export default function DriverMissionDetail({ missionId, onBack }) {
  const { fmt } = useFormat();
  const [mission, setMission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadMission = useCallback(async () => {
    try {
      setLoading(true);
      const res = await driverMonoAPI.getMissionDetail(missionId);
      setMission(res.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [missionId]);

  useEffect(() => {
    loadMission();
  }, [loadMission]);

  const handleAction = async (action) => {
    try {
      setActionLoading(true);
      setError(null);
      switch (action) {
        case 'accept':
          await driverMonoAPI.acceptMission(missionId);
          break;
        case 'start':
          await driverMonoAPI.startMission(missionId);
          break;
        case 'complete':
          await driverMonoAPI.completeMission(missionId);
          break;
        default:
          break;
      }
      await loadMission();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadFiche = () => {
    const url = `${API_BASE_URL}/missions/${missionId}/fiche-pdf`;
    const headers = getAuthHeaders();
    window.open(url + `?token=${encodeURIComponent(headers['Authorization']?.replace('Bearer ', '') || '')}`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!mission) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-700 dark:text-red-400">Mission introuvable</p>
        <button onClick={onBack} className="mt-2 text-blue-600 dark:text-blue-400 hover:underline text-sm">Retour</button>
      </div>
    );
  }

  const trajets = mission.trajets || [];

  return (
    <div className="space-y-4">
      {/* Back button + Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
          title="Retour"
        >
          <ArrowLeft className="h-5 w-5 text-gray-700 dark:text-slate-300" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-gray-400 dark:text-slate-500">{mission.code}</span>
            <MissionStatusBadge status={mission.status} />
          </div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">{mission.title}</h2>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
          <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 flex-wrap">
        {mission.status === 'assigned' && (
          <button
            onClick={() => handleAction('accept')}
            disabled={actionLoading}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            <CheckCircle className="h-4 w-4" />
            Accepter la mission
          </button>
        )}
        {mission.status === 'accepted' && (
          <button
            onClick={() => handleAction('start')}
            disabled={actionLoading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <Play className="h-4 w-4" />
            Démarrer la mission
          </button>
        )}
        {mission.status === 'in_progress' && (
          <button
            onClick={() => handleAction('complete')}
            disabled={actionLoading}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
          >
            <CheckCircle className="h-4 w-4" />
            Terminer la mission
          </button>
        )}
        <button
          onClick={handleDownloadFiche}
          className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
        >
          <Download className="h-4 w-4" />
          Fiche de Mission
        </button>
      </div>

      {/* Mission Info Card */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        {mission.client_name && (
          <div>
            <p className="text-xs text-gray-500 dark:text-slate-400">Client</p>
            <p className="font-medium text-gray-900 dark:text-white">{mission.client_name}</p>
            {mission.client_address && <p className="text-sm text-gray-500 dark:text-slate-400">{mission.client_address}</p>}
          </div>
        )}
        {mission.planned_start && (
          <div>
            <p className="text-xs text-gray-500 dark:text-slate-400">Début planifié</p>
            <p className="font-medium text-gray-900 dark:text-white">
              {fmt.date(mission.planned_start, {
                day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
              })}
            </p>
          </div>
        )}
        {mission.planned_end && (
          <div>
            <p className="text-xs text-gray-500 dark:text-slate-400">Fin planifiée</p>
            <p className="font-medium text-gray-900 dark:text-white">
              {fmt.date(mission.planned_end, {
                day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
              })}
            </p>
          </div>
        )}
        {mission.priority && (
          <div>
            <p className="text-xs text-gray-500 dark:text-slate-400">Priorité</p>
            <p className="font-medium text-gray-900 dark:text-white capitalize">{mission.priority}</p>
          </div>
        )}
        {mission.description && (
          <div className="md:col-span-2">
            <p className="text-xs text-gray-500 dark:text-slate-400">Description</p>
            <p className="text-sm text-gray-700 dark:text-slate-300">{mission.description}</p>
          </div>
        )}
        {mission.notes && (
          <div className="md:col-span-2">
            <p className="text-xs text-gray-500 dark:text-slate-400">Notes</p>
            <p className="text-sm text-gray-700 dark:text-slate-300">{mission.notes}</p>
          </div>
        )}
      </div>

      {/* Trajet Timeline */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-5">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          Trajets ({trajets.length})
        </h3>

        {trajets.length === 0 ? (
          <p className="text-gray-400 dark:text-slate-500 text-sm">Aucun trajet planifié</p>
        ) : (
          <div className="space-y-3">
            {trajets.map((trajet, idx) => (
              <div
                key={trajet.id}
                className={`flex items-start gap-3 p-3 rounded-lg border ${
                  trajet.status === 'in_progress' ? 'border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20' :
                  trajet.status === 'completed' ? 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20' :
                  trajet.status === 'skipped' ? 'border-gray-200 bg-gray-50 dark:border-slate-700 dark:bg-slate-800/50 opacity-60' :
                  'border-gray-200 dark:border-slate-700'
                }`}
              >
                {/* Sequence indicator */}
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                  trajet.status === 'completed' ? 'bg-green-600 text-white' :
                  trajet.status === 'in_progress' ? 'bg-blue-600 text-white' :
                  'bg-gray-300 dark:bg-slate-600 text-white'
                }`}>
                  {trajet.status === 'completed' ? <CheckCircle className="h-3.5 w-3.5" /> : trajet.sequence_order}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {trajet.from_label || 'Départ'} → {trajet.to_label || 'Arrivée'}
                    </span>
                    <span className={`px-1.5 py-0.5 text-xs rounded font-medium ${
                      trajet.status === 'in_progress' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                      trajet.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                      trajet.status === 'skipped' ? 'bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-slate-400' :
                      'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400'
                    }`}>
                      {trajet.status === 'planned' ? 'Planifié' :
                       trajet.status === 'in_progress' ? 'En cours' :
                       trajet.status === 'completed' ? 'Terminé' :
                       'Ignoré'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 dark:text-slate-400">
                    {trajet.distance_planned_km && (
                      <span>{trajet.distance_planned_km} km planifiés</span>
                    )}
                    {trajet.distance_actual_km && (
                      <span className="text-green-600 dark:text-green-400">{trajet.distance_actual_km} km réels</span>
                    )}
                    {trajet.purpose && (
                      <span>{trajet.purpose}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mission totals */}
      {(mission.total_km > 0 || mission.status === 'completed') && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-4 flex items-center gap-6">
          <div>
            <p className="text-xs text-gray-500 dark:text-slate-400">Distance totale</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white">{Math.round(mission.total_km || 0)} km</p>
          </div>
          {mission.actual_start && mission.actual_end && (
            <div>
              <p className="text-xs text-gray-500 dark:text-slate-400">Durée</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {Math.round((new Date(mission.actual_end) - new Date(mission.actual_start)) / (1000 * 60 * 60))}h
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
